import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule, NgModel } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { ToastrService } from 'ngx-toastr';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';

import {
  AssociationDescriptionDto,
  Client,
  OnDemandReadRegisterIn, StringResponseCommandSuccess,
} from '../../../api/api-reference';

@Component({
  selector: 'app-on-demand-custom-register-setup',
  standalone: true,
  imports: [FormsModule, CommonModule, AngularSvgIconModule, TranslateModule],
  providers: [DatePipe],
  templateUrl: './on-demand-custom-register-setup.html',
  styleUrl: './on-demand-custom-register-setup.scss',
})
export class OnDemandCustomRegisterSetupComponent implements OnInit, OnDestroy {
  associations: AssociationDescriptionDto[] = [];
  requestData: OnDemandReadRegisterIn = new OnDemandReadRegisterIn();
  currentRouteSegment: string | undefined;

  private meterNoChanges$ = new Subject<{ value: string; control: NgModel }>();
  private meterNoSub!: Subscription;
  public registerListFetchInProgress = false;

  constructor(
    private client: Client,
    private toastr: ToastrService,
    private router: Router,
  ) {}

  ngOnInit() {
    this.requestData.meterNo = '';
    const urlPart = this.router.url.split('/').pop() || '';
    this.currentRouteSegment = urlPart.replace('-setup', '') || '';

    this.meterNoSub = this.meterNoChanges$
      .pipe(
        debounceTime(1000),
        distinctUntilChanged((prev, curr) => prev.value === curr.value),
        filter(({ value, control }) => !!control.valid && value.trim() !== ''),
      )
      .subscribe(({ value }) => {
        this.client.getMeterModelHesId(value).subscribe({
          next: (meterModelHesId: string) => {
            this.client
              .getOnDemandRegisterAttributes(meterModelHesId)
              .subscribe({
                next: (registers: AssociationDescriptionDto[]) => {
                  this.associations = registers;
                  this.registerListFetchInProgress = false;
                },
                error: (err) => {
                  this.associations = [];
                  this.toastr.error(
                    'Failed to get allowed registers for meter model. ' + err,
                  );
                  this.registerListFetchInProgress = false;
                },
              });
          },
          error: (e) => {
            this.associations = [];
            this.toastr.error('Failed to get meter model. ' + e);
          },
        });
      });
  }

  onMeterNoChange(value: string, control: NgModel): void {
    this.requestData.meterNo = value;
    this.registerListFetchInProgress = true;
    if (value.trim() === '') {
      this.associations = [];
    }
    this.meterNoChanges$.next({ value, control });
  }

  toggleRow(index: number): void {
    const association = this.associations[index];
    if (association != null) {
      association.inUse = !association.inUse;
    }
  }

  onSubmit() {
    this.requestData.attributesList = this.associations
      .filter((a) => a.inUse)
      .map((a) => a.attributes);

    this.client.onDemandReadRegister(this.requestData).subscribe({
      next: () => {
        this.router.navigate(['/on-demand/custom-register']);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  onBack() {
    this.router.navigate(['/on-demand/custom-register']);
  }

  ngOnDestroy(): void {
    this.meterNoSub?.unsubscribe();
  }

  get isAnyCheckboxChecked(): boolean {
    return this.associations.some((a) => a.inUse);
  }
}
